import React, { useEffect, useState, ReactNode } from 'react';
import { Card } from 'react-bootstrap';
import { useParams, useLocation } from 'react-router';
import * as Api from '../../../api';
import { TApiSubset } from '../../../api/endpoints/Subset';
import { TApiSubsetOptions } from '../../../api/endpoints/SubsetOption';
import ProductForm, { IProductFormValues } from '../ProductForm';
import { FormikProps, FormikHelpers } from 'formik';
import queryString, { ParsedQuery } from 'query-string'
import { fetchSubsetOptions } from '../../../store/action-creators/subset-options';
import { useDispatch, useSelector } from "react-redux";
import { selectSubsetOptions } from "../../../store/selectors/new/subsetOptions";
import LoadingIndicator from "../../Feedback/LoadingIndicator";

interface IProps {
  subsetId?: number;
  initialValues?: IProductFormValues;
  actions?: (subset: TApiSubset, subsetOptions: TApiSubsetOptions, formik: FormikProps<IProductFormValues>) => ReactNode;
  onChange?: (values: IProductFormValues) => void;
  onSubmit?: (
    subset: TApiSubset,
    subsetOptions: TApiSubsetOptions,
    values: IProductFormValues,
    formikHelpers: FormikHelpers<IProductFormValues>) => void;
}

const useQuery = () => {
  const { search } = useLocation();

  return queryString.parse(search);
}

const parseQueryParams = (query: ParsedQuery | null | undefined): Array<string> => {
  const params = query?.params;

  if (!params) {
    return [];
  }

  return (Array.isArray(params) ? params : [params]).map(p => p.toUpperCase());
}

const SubsetOptionsSelect = (props: IProps) => {
  const { subsetId, initialValues, actions, onChange, onSubmit } = props;
  const dispatch = useDispatch();
  const params = useParams<{ subsetId: string }>();
  const query = useQuery();
  const queryParameters = parseQueryParams(query);

  const id = subsetId ?? params.subsetId;

  const [subset, setSubset] = useState<TApiSubset | null>(null);
  const subsetOptions = useSelector(selectSubsetOptions(parseInt(id.toString(), 10)));

  useEffect(() => {
    Api.Subset.fetch(id).then(subset => {
      setSubset(subset);
    });
  }, [id]);

  useEffect(() => {
    if (subset) {
      dispatch(fetchSubsetOptions(subset.id));
    } else {
      setSubset(null);
    }
  }, [dispatch, subset]);

  const onProductSubmit = (values: IProductFormValues, formikHelpers: FormikHelpers<IProductFormValues>) => {
    onSubmit && subset && subsetOptions && onSubmit(subset, subsetOptions, values, formikHelpers);
  }

  if (!subset || !subsetOptions) {
    return <LoadingIndicator />
  }

  const initialValuesWithInjectedParams = {
    ...initialValues,
    ...(queryParameters.length && { param: queryParameters })
  }

  return (
    <Card className="product-configurator-card">
      <Card.Body>
        {
          <ProductForm
            subset={subset}
            initialValues={initialValuesWithInjectedParams}
            onSubmit={onProductSubmit}
            onChange={onChange}
            subsetOptions={subsetOptions}
            actions={actions}
            includeLocation={Boolean(subset?.list_of_choices.includes('location'))}
          />
        }
      </Card.Body>
    </Card>
  );
};

export default SubsetOptionsSelect;
